import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Circle, XCircle, Loader2 } from 'lucide-react';
import { bestDescriptor, distance, runLiveness } from '../faceKit';
import { useStreamVideo } from '../media';

const PROMPTS = { blink: 'Slowly blink your eyes', turn: 'Turn your head slightly left or right, then back' };
const SAME_PERSON_MAX = 0.6;

const StepIcon = ({ status }) => {
  if (status === 'passed') return <CheckCircle2 size={18} color="#10B981" />;
  if (status === 'failed') return <XCircle size={18} color="#EF4444" />;
  if (status === 'active') return <Loader2 size={18} color="#0076A3" className="animate-spin" />;
  return <Circle size={18} color="#94A3B8" />;
};

export default function LivenessCheck({ stream, kitReady, selfieDescriptor, onDone }) {
  const [, setVideo] = useStreamVideo(stream);
  const videoEl = useRef(null);
  const cancelled = useRef(false);
  const [steps, setSteps] = useState({});
  const [active, setActive] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const bind = (el) => { videoEl.current = el; setVideo(el); };

  useEffect(() => {
    cancelled.current = false;
    return () => { cancelled.current = true; };
  }, []);

  const start = async () => {
    setRunning(true);
    setResult(null);
    setSteps({});
    const base = await runLiveness(videoEl.current, {
      isCancelled: () => cancelled.current,
      onStep: (type, status) => { setSteps((s) => ({ ...s, [type]: status })); setActive(status === 'active' ? type : null); },
    });
    let same = null;
    try {
      const d = await bestDescriptor(videoEl.current, 320);
      same = distance(d.descriptor, selfieDescriptor);
    } catch (e) { same = null; }
    const passed = base.passed && (same === null || same <= SAME_PERSON_MAX);
    setResult({ ...base, passed, same_person_distance: same });
    setRunning(false);
  };

  if (!kitReady) {
    return (
      <div data-testid="liveness-unavailable">
        <div className="iv-note is-warn">The liveness check can't run on this device. That's fine: a person from the hiring team will review your ID and selfie instead.</div>
        <div className="iv-row-end">
          <button type="button" className="iv-btn iv-btn-primary" onClick={() => onDone({ passed: false, reason: 'unavailable', challenges: [] })} data-testid="liveness-continue-btn">Continue</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="liveness-check">
      <div className="iv-video">
        <video ref={bind} className="is-mirror" autoPlay muted playsInline data-testid="liveness-camera-preview" />
        <div className="iv-oval" />
        <div className="iv-prompt" data-testid="liveness-prompt">{active ? PROMPTS[active] : running ? 'Hold still…' : 'Look at the camera'}</div>
      </div>
      <div className="iv-status-list">
        {['blink', 'turn'].map((t) => (
          <div key={t} className="iv-status" data-testid={`liveness-step-${t}`}>
            <span>{PROMPTS[t]}</span><StepIcon status={steps[t]} />
          </div>
        ))}
      </div>
      {result && (
        <div className={`iv-note ${result.passed ? 'is-good' : 'is-warn'}`} style={{ marginTop: '0.75rem' }} data-testid="liveness-result">
          {result.passed ? 'Liveness confirmed.' : 'We could not confirm every step. You can try again, or continue and a person will review.'}
        </div>
      )}
      <div className="iv-row-end">
        {!result && (
          <button type="button" className="iv-btn iv-btn-primary" disabled={running} onClick={start} data-testid="liveness-start-btn">
            {running ? 'Checking…' : 'Start liveness check'}
          </button>
        )}
        {result && !result.passed && (
          <button type="button" className="iv-btn iv-btn-ghost" onClick={start} data-testid="liveness-retry-btn">Try again</button>
        )}
        {result && (
          <button type="button" className="iv-btn iv-btn-primary" onClick={() => onDone(result)} data-testid="liveness-continue-btn">Continue</button>
        )}
      </div>
    </div>
  );
}
